import { useState, useEffect, useCallback } from "react";
import {
  Switch, Button, Table, Tag, Modal, message, Typography,
} from "antd";
import StatusBadge from "../../../components/common/StatusBadge";
import PageLoader from "../../../components/common/PageLoader";
import {
  listAdminUsers,
  forceResetPassword,
} from "../../../services/adminUsers.service";

const { Text } = Typography;

export default function SecurityPasswordsTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [securityState] = useState({
    forceResetOnLogin: true,
    twoFactorEnabled: false,
    sessionTimeout: 480,
  });

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetting, setResetting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listAdminUsers({ per_page: 100 });
      setUsers(result.records || []);
    } catch {
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleResetPW = (user) => {
    setResetUser(user);
    setResetModalOpen(true);
  };

  const handleResetConfirm = async () => {
    if (!resetUser?._id) return;
    setResetting(true);
    try {
      const result = await forceResetPassword(resetUser._id);
      if (result?.emailSent === false) {
        message.warning(
          `Password reset, but email failed to send. ${result.emailError || "Check SMTP settings."}`
        );
      } else {
        message.success("Password reset successfully. A new temporary password has been emailed to the user.");
      }
      setResetModalOpen(false);
      setResetUser(null);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <PageLoader tip="Loading..." />;

  const columns = [
    {
      title: "User Name",
      key: "name",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%", background: "#0f172a",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 11, fontWeight: 700,
          }}>
            {record.firstName?.[0]}{record.lastName?.[0]}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 12, color: "#0f172a" }}>
              {record.firstName} {record.lastName}
            </div>
            <div style={{ fontSize: 10, color: "#9ca3af" }}>{record.role}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Login ID / Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <Text style={{ fontSize: 12 }}>{email}</Text>,
    },
    {
      title: "Last Login",
      dataIndex: "lastLogin",
      key: "lastLogin",
      render: (val) => (
        <Text style={{ fontSize: 12, color: "#64748b" }}>
          {val ? new Date(val).toLocaleString() : "Never"}
        </Text>
      ),
    },
    {
      title: "Password",
      key: "password",
      render: () => <Text style={{ fontSize: 12, color: "#64748b" }}>••••••••</Text>,
    },
    {
      title: "2FA",
      dataIndex: "twoFactorEnabled",
      key: "twoFactorEnabled",
      render: (val) => (
        <Tag color={val ? "green" : "default"} style={{ fontSize: 10, fontWeight: 600 }}>
          {val ? "Enabled" : "Disabled"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StatusBadge status={status} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          style={{ fontSize: 10, fontWeight: 600 }}
          onClick={() => handleResetPW(record)}
        >
          Reset PW
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>Security & Passwords</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>Manage login credentials and password policy</Text>
        </div>
      </div>

      <div style={{
        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6,
        padding: 20, marginBottom: 20, display: "flex", gap: 48,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch checked={securityState.forceResetOnLogin} disabled />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>Force Reset on First Login</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>New users must change password</div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch checked={false} disabled />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af" }}>Two-Factor Authentication</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>Coming soon</div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch checked disabled />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af" }}>Session Timeout</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>8 hrs (fixed)</div>
          </div>
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="_id"
          pagination={false}
          size="middle"
          className="data-table"
        />
      </div>

      <Modal
        title="Reset Password"
        open={resetModalOpen}
        onOk={handleResetConfirm}
        onCancel={() => {
          setResetModalOpen(false);
          setResetUser(null);
        }}
        confirmLoading={resetting}
        okText="Reset & Send Email"
      >
        <p style={{ marginBottom: 12, fontSize: 13, color: "#64748b" }}>
          A new temporary password will be generated and emailed to{" "}
          <strong>{resetUser?.email}</strong>. The user will be required to change it on their next login.
        </p>
      </Modal>
    </div>
  );
}
